import React from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { LinearStepper } from "../../components/ui";
import { CAMPAIGN_FLOW_STEPS } from "./campaignFlow";
import {
  clearPersistedDraftCampaignId,
  fetchCampaignDraft,
  resolveFlowScope,
  type CampaignFlowState,
} from "./campaignDraftFlow";
import { ownerSelector } from "../../store/store";

function getCurrentStepFromPath(pathname: string) {
  if (pathname.includes("/campaign-management/create/senders")) return 1;
  if (pathname.includes("/campaign-management/create/sequence")) return 2;
  if (pathname.includes("/campaign-management/create/schedule")) return 3;
  if (pathname.includes("/campaign-management/create/review-launch")) return 4;
  return 0;
}

export default function CampaignCreateLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const currentStep = React.useMemo(() => getCurrentStepFromPath(location.pathname), [location.pathname]);

  const { owner, currentUser, currentWorkspace } = useSelector(ownerSelector);
  const flowState = (location.state as CampaignFlowState | null) || {};
  const [searchParams] = React.useState(() => new URLSearchParams(location.search));
  const campaignId = flowState.campaignId || searchParams.get("campaignId") || undefined;
  const [draftCampaign, setDraftCampaign] = React.useState<any>(null);

  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const hasExplicitCampaignId = Boolean(
      searchParams.get("campaignId") || (location.state as { campaignId?: string } | null)?.campaignId
    );
    const isFreshCreateEntry = location.pathname === "/campaign-management/create";
    if (isFreshCreateEntry && !hasExplicitCampaignId) {
      clearPersistedDraftCampaignId();
    }
  }, [location.pathname, location.search, location.state]);

  const resolveScope = React.useCallback(() => {
    return resolveFlowScope({
      flowState,
      owner,
      currentUser,
      currentWorkspace,
    });
  }, [flowState, owner, currentUser, currentWorkspace]);

  React.useEffect(() => {
    const hydrateDraft = async () => {
      if (!campaignId) {
        setDraftCampaign(null);
        return;
      }
      // If we already have step payloads in memory, no need to fetch just for stepper coloring.
      const hasAnyStepPayload =
        Boolean(flowState.stepOnePayload) ||
        Boolean(flowState.stepTwoPayload) ||
        Boolean(flowState.stepThreePayload) ||
        Boolean(flowState.stepFourPayload);
      if (hasAnyStepPayload) return;

      try {
        const { userId, workspaceId, ownerId } = resolveScope();
        const draftResponse = await fetchCampaignDraft({
          campaignId,
          ownerId,
          userId,
          workspaceId,
        });
        setDraftCampaign(draftResponse?.campaign || null);
      } catch {
        setDraftCampaign(null);
      }
    };
    void hydrateDraft();
  }, [
    campaignId,
    flowState.stepFourPayload,
    flowState.stepOnePayload,
    flowState.stepThreePayload,
    flowState.stepTwoPayload,
    resolveScope,
  ]);

  const maxNavigableStep = React.useMemo(() => {
    // Stepper indices:
    // 0 leads, 1 senders, 2 sequence, 3 schedule, 4 review
    // "maxNavigableStep" means: highest step index user can jump to (green).
    let max = -1;

    // In-memory progress (during a single flow session).
    if (flowState.stepOnePayload) max = Math.max(max, 0);
    if (flowState.stepTwoPayload) max = Math.max(max, 1);
    if (flowState.stepThreePayload) max = Math.max(max, 2);
    if (flowState.stepFourPayload) max = Math.max(max, 3);

    // Server draft progress (edit/reload case when location.state is missing).
    if (draftCampaign) {
      const hasStepOne =
        Boolean(String(draftCampaign?.campaignName || "").trim()) &&
        (Boolean(draftCampaign?.listId) ||
          (Array.isArray(draftCampaign?.listIds) && draftCampaign.listIds.length > 0));

      // NOTE: backend draft does not currently expose `executorOwnerId` / `senderOwnerIds`,
      // so the best available proxy is whether `ownerId` exists.
      // This still won't mark later steps complete (which was the bug).
      const hasStepTwo = hasStepOne && Boolean(draftCampaign?.ownerId);

      const hasStepThree =
        (Array.isArray(draftCampaign?.actions) &&
          draftCampaign.actions.length > 0) ||
        Boolean(draftCampaign?.sequenceRehydration);

      // IMPORTANT: schedule defaults always exist on the model, so only treat schedule as
      // completed when user actually set meaningful values.
      const scheduleMode = String(draftCampaign?.scheduleMode || "").toUpperCase();
      const schedule = draftCampaign?.schedule || {};
      const hasMeaningfulSchedule =
        scheduleMode === "CAMPAIGN_CUSTOM" ||
        Boolean(String(schedule?.timezone || "").trim()) ||
        Boolean(String(schedule?.from || "").trim()) ||
        Boolean(String(schedule?.to || "").trim()) ||
        (Array.isArray(schedule?.days) && schedule.days.length > 0) ||
        Boolean(schedule?.startDate) ||
        Boolean(schedule?.endDate);

      if (hasStepOne) max = Math.max(max, 0);
      if (hasStepTwo) max = Math.max(max, 1);
      if (hasStepThree) max = Math.max(max, 2);
      if (hasMeaningfulSchedule) max = Math.max(max, 3);
    }

    // Always allow clicking the current step.
    return Math.max(max, currentStep);
  }, [currentStep, draftCampaign, flowState.stepFourPayload, flowState.stepOnePayload, flowState.stepThreePayload, flowState.stepTwoPayload]);

  const handleStepClick = React.useCallback(
    (stepIndex: number) => {
      const target = CAMPAIGN_FLOW_STEPS[stepIndex];
      if (!target) return;
      const base = "/campaign-management/create";
      const pathname = target.path ? `${base}/${target.path}` : base;
      navigate(
        {
          pathname,
          search: campaignId ? `?campaignId=${encodeURIComponent(campaignId)}` : location.search,
        },
        { state: flowState }
      );
    },
    [campaignId, flowState, location.search, navigate]
  );

  return (
    <section
      className="flex h-full min-h-0 flex-col overflow-hidden bg-background-grey px-4 pb-4 sm:px-6 sm:pb-6"
    >
      <header className="shrink-0 py-6 pt-8">
        <h1 className="text-xl font-semibold text-dark-black">My Campaigns</h1>
        <p className="mt-2 text-sm text-dark-grey">View and manage your campaigns</p>
      </header>

      <div
        className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[8px] border border-light-grey bg-white"
      >
        <div className="overflow-x-auto border-b border-light-grey px-4 py-4 sm:px-6 sm:py-5">
          <LinearStepper
            steps={CAMPAIGN_FLOW_STEPS.map((step) => ({ id: step.id, label: step.label }))}
            currentStep={currentStep}
            maxNavigableStep={maxNavigableStep}
            onStepClick={handleStepClick}
          />
        </div>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <Outlet />
        </div>
      </div>
    </section>
  );
}
